import React, { useState, useRef, useEffect } from 'react';
import api, { API_URL } from '../../api';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { Upload03Icon, CancelCircleIcon } from 'hugeicons-react';
import '../../assets/css/user/UserImport.css';


const UserImport = ({ onUsersImported, onClose, embedded = false, show = false }) => {
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    const [currentStep, setCurrentStep] = useState(1);
    const [mappedFields, setMappedFields] = useState({
        email: 'email',
        first_name: 'first_name',
        last_name: 'last_name',
        position: 'position',
        hospital: 'hospital'
    });
    const [previewData, setPreviewData] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [importedCount, setImportedCount] = useState(0);
    const [showModal, setShowModal] = useState(show);

    const [fileUploaded, setFileUploaded] = useState(false);
    const [csvColumns, setCsvColumns] = useState([]);

    const fileInputRef = useRef(null);
    const dropAreaRef = useRef(null);

    // Fix useEffect to sync show prop with internal state
    useEffect(() => {
        if (show && !showModal) {
            // Reset state when opening modal
            setFile(null);
            setCurrentStep(1);
            setPreviewData(null);
            setImportedCount(0);
            setFileUploaded(false);
            setCsvColumns([]);
            setImportResult(null); // Reset import result
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
        setShowModal(show);
    }, [show]); // Remove showModal from dependencies to prevent infinite loop


    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setFileUploaded(true);
            toast.success('File uploaded. You can now proceed to map fields and upload users.');
            // If CSV, parse columns robustly
            if (selectedFile.name.endsWith('.csv')) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const text = event.target.result;
                    // Handle both Windows (\r\n) and Unix (\n) line endings
                    const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
                    if (lines.length > 0) {
                        let columns = lines[0].split(',').map(col => col.trim().replace(/^"|"$/g, ''));
                        setCsvColumns(columns);
                    } else {
                        setCsvColumns([]);
                    }
                };
                reader.readAsText(selectedFile);
            } else if (selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls')) {

                import('xlsx').then(XLSX => {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        const data = new Uint8Array(event.target.result);
                        const workbook = XLSX.read(data, { type: 'array' });
                        const firstSheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[firstSheetName];
                        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                        if (json.length > 0) {
                            setCsvColumns(json[0].map(col => String(col).trim()));
                        } else {
                            setCsvColumns([]);
                        }
                    };
                    reader.readAsArrayBuffer(selectedFile);
                }).catch(() => {
                    toast.error('Failed to parse Excel file.');
                    setCsvColumns([]);
                });
            } else {
                setCsvColumns([]);
            }
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (dropAreaRef.current) {
            dropAreaRef.current.classList.add('drag-over');
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        if (dropAreaRef.current) {
            dropAreaRef.current.classList.remove('drag-over');
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (dropAreaRef.current) {
            dropAreaRef.current.classList.remove('drag-over');
        }

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const droppedFile = e.dataTransfer.files[0];
            setFile(droppedFile);
            // Trigger the same logic as handleFileChange
            const event = { target: { files: [droppedFile] } };
            handleFileChange(event);
        }
    };

    const handleFieldMapping = (sourceField, targetField) => {
        setMappedFields(prev => ({
            ...prev,
            [targetField]: sourceField
        }));
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error('Please select a file');
            return;
        }

        setIsUploading(true);

        try {

            if (file.name.endsWith('.csv')) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const text = event.target.result;
                    const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');

                    const count = Math.max(0, lines.length - 1);
                    setImportedCount(count);
                    setCurrentStep(3);
                };
                reader.readAsText(file);
            } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {

                import('xlsx').then(XLSX => {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        const data = new Uint8Array(event.target.result);
                        const workbook = XLSX.read(data, { type: 'array' });
                        const firstSheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[firstSheetName];
                        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                        const count = Math.max(0, json.length - 1);
                        setImportedCount(count);
                        setCurrentStep(3);
                    };
                    reader.readAsArrayBuffer(file);
                }).catch(() => {
                    toast.error('Failed to parse Excel file.');
                    setImportedCount(0);
                    setCurrentStep(3);
                });
            } else {

                setImportedCount(0);
                setCurrentStep(3);
            }
        } catch (error) {
            console.error('Error processing file:', error);
            toast.error('Error processing file');
        } finally {
            setIsUploading(false);
        }
    };

    const confirmImport = async () => {
        setIsUploading(true);

        try {
            const formData = new FormData();
            formData.append('file', file);

            Object.entries(mappedFields).forEach(([key, value]) => {
                formData.append(`mapping[${key}]`, value);
            });

            const token = Cookies.get('accessToken');

            const response = await api.post(`${API_URL}/bulk-import/users/upload/`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.status === 200) {
                setImportResult({ type: 'success', data: response.data });
                setCurrentStep(4);
                if (typeof onUsersImported === 'function') {
                    onUsersImported(response.data);
                }
            } else {
                toast.error('Failed to import users');
            }
        } catch (error) {
            console.error('Error importing users:', error);

            // Extract specific error message from API response
            let errorMessage = 'Error importing users';
            let errorData = null;
            if (error.response && error.response.data) {
                errorData = error.response.data;
                if (errorData.error) {
                    errorMessage = errorData.error;
                } else if (errorData.details && errorData.details.error) {
                    errorMessage = errorData.details.error;
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                }
            }

            // If we have structured error data (like lists of existing users), show it in Step 4
            if (errorData && (errorData.existing || errorData.duplicates || errorData.errors || Array.isArray(errorData))) {
                setImportResult({ type: 'error', data: errorData, message: errorMessage });
                setCurrentStep(4);
            } else {
                toast.error(errorMessage);
            }
        } finally {
            setIsUploading(false);
        }
    };



    const handleModalClose = () => {
        setFile(null);
        setCurrentStep(1);
        setPreviewData(null);
        setImportedCount(0);
        setShowModal(false);
        setFileUploaded(false);
        setCsvColumns([]);
        setImportResult(null); // Reset import result on close
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        if (typeof onClose === 'function') {
            onClose();
        }
    };

    const handleClose = embedded ? () => {
        // In embedded mode, close the parent form
        if (typeof onClose === 'function') {
            onClose();
        }
    } : handleModalClose;

    const goToList = () => {
        handleClose();

        if (!embedded) {
            window.location.href = '/users-list';
        }
    };



    const handleBrowseClick = (e) => {
        e.stopPropagation();

        if (fileInputRef.current) {
            fileInputRef.current.click();
        } else {
            console.error("File input reference is null");
            toast.error("Couldn't open file dialog. Please try again.");
        }
    };


    const renderStep = () => {
        // Step tabs
        const steps = [
            { id: 1, label: 'Upload File' },
            { id: 2, label: 'Map Fields' },
            { id: 3, label: 'Confirm Import' },
            { id: 4, label: 'Result' }
        ];

        if (currentStep === 1) {
            return (
                <div className="import-step step-1">
                    <div style={{ width: '100%', textAlign: 'center' }}>
                        <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                            Don't have the file format? <span
                                onClick={(e) => {
                                    e.stopPropagation();
                                    import('xlsx').then(XLSX => {
                                        const headers = ['first_name', 'last_name', 'email', 'position', 'hospital'];
                                        const ws = XLSX.utils.aoa_to_sheet([headers]);
                                        const wb = XLSX.utils.book_new();
                                        XLSX.utils.book_append_sheet(wb, ws, "Template");
                                        XLSX.writeFile(wb, "user_import_template.xlsx");
                                    }).catch(err => {
                                        console.error("Failed to load xlsx", err);
                                        toast.error("Failed to generate Excel template");
                                    });
                                }}
                                style={{ color: '#07AEEF', cursor: 'pointer', textDecoration: 'underline', fontWeight: 500 }}
                            >
                                Download Excel template
                            </span>
                        </p>
                    </div>
                    <div className="step-tabs">
                        {steps.map((step) => (
                            <div
                                key={step.id}
                                className={`step-tab ${currentStep >= step.id ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
                            >
                                <span className="step-number">{step.id}</span>
                                <span className="step-label">{step.label}</span>
                            </div>
                        ))}
                    </div>
                    {/* <div className='title'>
                        <h2>Import users steps</h2>
                    </div> */}
                    <div className="import-content">
                        <h3>Import .csv, .xls and .xlsx files only </h3>


                        <div
                            className="drop-area"
                            ref={dropAreaRef}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={handleBrowseClick}
                        >
                            <Upload03Icon size={32} color='#000000' />
                            <p>Drag or drop to upload</p>
                            <p onClick={handleBrowseClick} className='browse-btn'>or browse files </p>
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept=".csv, .xlsx"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                        </div>
                        {fileUploaded && (
                            <div className="file-uploaded-notification">
                                <span style={{ color: 'green', fontWeight: 500 }}>File uploaded successfully!</span>
                            </div>
                        )}
                        <button
                            className="upload-btn"
                            onClick={() => file ? setCurrentStep(2) : handleBrowseClick()}
                            disabled={isUploading || !file}
                        >
                            {isUploading ? 'Uploading...' : 'Upload users'}
                        </button>
                    </div>
                </div>
            );
        }

        if (currentStep === 2) {
            return (
                <div className="import-step step-2">
                    <div className='title'>
                        <h2>Import users</h2>
                        {embedded && (
                            <div className="close-btn" onClick={handleClose}>
                                <CancelCircleIcon size={20} />
                            </div>
                        )}
                    </div>
                    <div className="step-tabs">
                        {steps.map((step) => (
                            <div
                                key={step.id}
                                className={`step-tab ${currentStep >= step.id ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
                            >
                                <span className="step-number">{step.id}</span>
                                <span className="step-label">{step.label}</span>
                            </div>
                        ))}
                    </div>

                    <div className="import-content">
                        {/* <h3>Users</h3> */}a
                        <p>These properties will be used to identify your users</p>
                        <div className="mapping-container">
                            <div className="mapping-header">
                                <span>Columns in your file</span>
                                <span>Properties on user list</span>
                            </div>

                            {csvColumns.length > 0 ? (
                                csvColumns.map((col, idx) => (
                                    <div className="mapping-row" key={col + idx}>
                                        <div className="file-column">{col}</div>
                                        <div className="arrow">→</div>
                                        <div className="property-column">
                                            <div className="file-column">{col}</div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div style={{ color: '#888', padding: '1em' }}>No columns detected. Please upload a valid CSV file.</div>
                            )}
                        </div>
                        <button
                            className="upload-btn"
                            onClick={handleUpload}
                            disabled={isUploading}
                        >
                            {isUploading ? 'Processing...' : 'Upload users'}
                        </button>
                    </div>
                </div>
            );
        }
        // Step 3: Confirmation
        if (currentStep === 3) {
            return (
                <div className="import-step step-3">
                    <div className="step-tabs">
                        {steps.map((step) => (
                            <div
                                key={step.id}
                                className={`step-tab ${currentStep >= step.id ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
                            >
                                <span className="step-number">{step.id}</span>
                                <span className="step-label">{step.label}</span>
                            </div>
                        ))}
                    </div>
                    <div className='title'>
                        <h2>Import users</h2>
                        {embedded && (
                            <div className="close-btn" onClick={handleClose}>
                                <CancelCircleIcon size={20} />
                            </div>
                        )}
                    </div>
                    <div className="import-content">
                        <h3>Users</h3>
                        <p>Ready to import users.</p>
                        <div className="preview-container">
                            <div className="count-display">
                                <h1>{importedCount}</h1>
                                <p>Users ready to import</p>
                            </div>
                        </div>
                        <div className="action-buttons">
                            <button
                                className="back-btn"
                                onClick={() => setCurrentStep(2)}
                            >
                                Back
                            </button>
                            <button
                                className="import-btn"
                                onClick={confirmImport}
                                disabled={isUploading}
                            >
                                {isUploading ? 'Importing...' : 'Import Users'}
                            </button>
                        </div>
                    </div>
                </div>
            );
        }
        // Step 4: Result (Success or Error with details)
        if (currentStep === 4) {
            const resultData = importResult?.data || {};
            const resultType = importResult?.type || 'success';

            // Helper to render list of items
            const renderList = (items, title, colorClass) => {
                if (!items || items.length === 0) return null;
                return (
                    <div className={`result-list ${colorClass}`} style={{ textAlign: 'left', marginTop: '15px', maxHeight: '150px', overflowY: 'auto', background: '#f9f9f9', padding: '10px', borderRadius: '4px' }}>
                        <h4 style={{ fontSize: '14px', marginBottom: '8px', color: '#333' }}>{title}:</h4>
                        <ul style={{ paddingLeft: '20px', margin: 0 }}>
                            {items.map((item, i) => (
                                <li key={i} style={{ fontSize: '13px', color: '#555' }}>
                                    {typeof item === 'object' ? JSON.stringify(item) : item}
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            };

            const existingUsers = resultData.existing || resultData.duplicates || resultData.existing_users;
            const errors = resultData.errors;
            const createdCount = resultData.created_count || resultData.created?.length || 0;
            const message = resultData.message || importResult?.message;

            return (
                <div className="import-step step-4">
                    <div className="step-tabs">
                        {steps.map((step) => (
                            <div
                                key={step.id}
                                className={`step-tab ${currentStep >= step.id ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
                            >
                                <span className="step-number">{step.id}</span>
                                <span className="step-label">{step.label}</span>
                            </div>
                        ))}
                    </div>
                    <div className="success-content" style={{ padding: '20px' }}>

                        {resultType === 'success' ? (
                            <>
                                <div className="success-icon" style={{ marginBottom: '10px' }}>
                                    <span style={{ fontSize: '48px', color: 'green' }}>✓</span>
                                </div>
                                <h2>Import Completed</h2>
                                <p>{message || "Operation completed."}</p>
                                {/* {createdCount > 0 && <p>{createdCount} users successfully imported.</p>} */}

                                {renderList(existingUsers, "The following users already exist", "warning")}
                                {renderList(errors, "Errors encountered", "error")}
                            </>
                        ) : (
                            <>
                                <div className="error-icon" style={{ marginBottom: '10px' }}>
                                    <CancelCircleIcon size={48} color="red" />
                                </div>
                                <h2 style={{ color: '#d32f2f' }}>Import Issues</h2>
                                <p>{message || "There were issues with the import."}</p>

                                {renderList(existingUsers, "Existing Users", "warning")}
                                {renderList(errors, "Errors", "error")}
                            </>
                        )}

                        <button
                            className="list-btn"
                            onClick={goToList}
                            style={{ marginTop: '20px' }}
                        >
                            Back to List
                        </button>
                    </div>
                </div>
            );
        }
        return null;
    };

    if (embedded) {
        return (
            <div className="user-import-embedded" onClick={e => e.stopPropagation()}>
                <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv, .xlsx, .xls"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                {renderStep()}
            </div>
        );
    }

    return (
        <div className="user-import-container">
            <input
                type="file"
                ref={fileInputRef}
                accept=".csv, .xlsx, .xls"
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
            {showModal && (
                <div
                    className="import-modal-overlay"
                    onClick={e => {
                        // Only close if click is directly on the overlay, not inside the modal
                        if (e.target.classList.contains('import-modal-overlay')) handleModalClose();
                    }}
                    style={{ zIndex: 9999 }}
                >
                    <div className="import-modal" onClick={e => e.stopPropagation()}>
                        {renderStep()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserImport;
